import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
} from "@material-ui/core"
import React from "react"
import { t } from "ttag"
import { $enum } from "ts-enum-util"
import { Label, PrimaryButton, Select, Tooltip } from "@humanpredictiveintelligence/myqvt-library"

import { useSessionStore } from "features/Session"
import { useUserPermissions } from "features/Permissions"
import { ExcelExportType } from "models/ExcelExportType"
import { USER_ATTRIBUTE } from "models/UserAttribute"
import { USER_ATTRIBUTE_VALUE } from "models/UserAttributeValue"
import { UserPermissionCode } from "models/generated"
import { arrayGroupedBy } from "utilities/helpers"
import * as Styles from "./ExcelExportDialog.styles"

export const ExcelExportDialog: React.FC<ExcelExportDialogProps> = ({
  status = "form", variant: exportVariant = "excel", ...props }) => {
  const languages = useSessionStore(state => state.languages)
  const user = useSessionStore(state => state.user)

  const isUserAllowed = useUserPermissions()
  const isUserAllowedToReadComments = isUserAllowed(UserPermissionCode.CommentsRead)

  const [ formState, setFormState ] = React.useState<Partial<ExportDialogForm>>({
    exportType: undefined,
    language: user!.language.code,
  })

  return (
    <Dialog open maxWidth="md">
      <DialogTitle disableTypography>
        {exportVariant === "excel" ? t`Excel export` : t`PowerPoint export`}
      </DialogTitle>
      <DialogContent>
        {status === "form" && (
          <>
            <div>
              {exportVariant === "excel" && (
                // eslint-disable-next-line max-len
                <>
                  {props.attributes && props.attributes.length > 0
                    ? t`Please select the content language and the type of data you wish to export.
                        Results will be filtered with the following filtering options:`
                    : t`Please select the content language and the type of data you wish to export.`
                  }
                  <br/>
                </>
              )}
              {exportVariant === "powerpoint" && props.attributes && props.attributes.length > 0 && (
                <>
                  {t`Results will be filtered with the following filtering options:`}
                  <br/>
                </>
              )}
              {props.attributes && props.attributes.length > 0 && formattedFiltersList(props.filters)}
            </div>
            <Styles.Form>
              {exportVariant === "excel" && (
                <Styles.SelectWrapper>
                  <Select
                    label={t`Content language`}
                    options={languages.map(language =>
                      ({ value: language.code, wording: language.translatedLabel }),
                    )}
                    value={formState.language}
                    onChange={(value) => setFormValue("language", value!.value as string)}
                    defaultItem={false}
                  />
                </Styles.SelectWrapper>
              )}

              {exportVariant === "excel" && (
                <div>
                  <Label text={t`Export type`}/>
                  <Styles.TypeRadioGroup
                    value={formState.exportType}
                    onChange={(_, value) => setFormValue("exportType", $enum(ExcelExportType).asValueOrDefault(value))}
                    row
                  >
                    <Tooltip
                      title={!isUserAllowedToReadComments && t`You do not have permission to select this field`}
                      placement="top-start"
                    >
                      <FormControlLabel
                        value={ExcelExportType.Comments}
                        control={<Radio/>}
                        disabled={!isUserAllowedToReadComments}
                        label={t`Comments`}
                      />
                    </Tooltip>
                    <FormControlLabel
                      value={ExcelExportType.Responses}
                      control={<Radio/>}
                      label={t`Answers distribution`}
                    />
                    <FormControlLabel
                      value={ExcelExportType.Participation}
                      control={<Radio/>}
                      label={t`Participation`}
                    />
                    <FormControlLabel
                      value={ExcelExportType.Ranking}
                      control={<Radio/>}
                      label={t`Ranking`}
                    />
                  </Styles.TypeRadioGroup>
                </div>
              )}
            </Styles.Form>
          </>
        )}
        {status === "confirmation" && (
          <div>{exportVariant === "excel"
            ? t`Your Excel export is being generated. You will receive an email when it is ready.`
            : t`Your PowerPoint export is being generated. You will receive an email when it is ready.`}</div>
        )}
      </DialogContent>
      <DialogActions>
        {status === "form" && <PrimaryButton isInverted onClick={props.onCloseRequested}>{t`Cancel`}</PrimaryButton>}
        <PrimaryButton onClick={confirm} disabled={!isFormValid(formState, exportVariant)}>
          {t`Confirm`}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  )

  /**
   * Set a form field value
   * @param field Form field to set
   * @param value Value to assign
   */
  function setFormValue<TField extends keyof ExportDialogForm>(
    field: TField,
    value: ExportDialogForm[TField] | undefined,
  ) {
    setFormState({
      ...formState,
      [field]: value,
    })
  }

  /**
   * Get whether the form is valid
   */
  function isFormValid(state: Partial<ExportDialogForm>, exportVariant: ExportVariant): state is ExportDialogForm {
    if (exportVariant === "powerpoint") {
      return !!state.language
    }
    return !!(state.language && state.exportType && $enum(ExcelExportType).isValue(state.exportType))
  }

  /**
   * Confirm the dialog
   */
  function confirm() {
    if (status === "form" && isFormValid(formState, exportVariant) && props.onExportRequested) {
      props.onExportRequested(formState as ExportDialogForm)
    }
    else if (status === "confirmation") {
      props.onCloseRequested()
    }
  }

  /**
   * Get a formatted list to display the active filters
   * @param filters Filters to list
   */
  function formattedFiltersList(filters?: ExcelExportDialogProps["filters"]): string {
    let displayText = t`No filter selected.`

    if (filters && filters.length > 0) {
      displayText = Array.from(arrayGroupedBy(filters, "attributeLabel"))
        .map(([ attributeLabel, values ]) => {
          const attributeName = attributeLabel.toLocaleUpperCase()
          const attributeValuesList = values.map(val => val.label).join(", ")

          return t`${attributeName}: ${attributeValuesList}`
        })
        .join(" - ")
    }

    return displayText
  }
}

export type ExportVariant = "excel" | "powerpoint"

export interface ExportDialogForm {
  language: string,
  exportType?: ExcelExportType,
}

export interface ExcelExportDialogForm extends ExportDialogForm {
  exportType: ExcelExportType,
}

export interface ExcelExportDialogProps {
  /** Applied filters to list in the dialog */
  filters: USER_ATTRIBUTE_VALUE[],

  /** List of available pivot attributes for the export */
  attributes?: USER_ATTRIBUTE[],

  /** Fired when the user validates the form */
  onExportRequested: (exportRequest: ExportDialogForm) => void,

  /** Fired when the user tries to cancel the form or after confirmation message */
  onCloseRequested: () => void,

  /** Whether the dialog shows the form or the confirmation message */
  status?: "form" | "confirmation",

  /** The type of export to display */
  variant?: ExportVariant,
}
